import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import PropTypes from 'prop-types'
import { useEffect, useState } from 'react'

import { Button } from '@/components/elements/button'
import { Quiz } from '@/components/Quiz'
import { QuizResult } from '@/components/QuizResult'
import { useInstructorView } from '@/pages/Accounts/Instructors/Students/InstructorViewContext'
import QuizAttemptsPanel from '@/pages/Accounts/Instructors/Students/QuizAttemptsPanel'
import { getQuizResult, takeStudentQuiz } from '@/services/student_courses.service'
import { course } from '@/stores/CourseStore'
import { scoreFormatter } from '@/utils/formatters'
import { toast } from '@/utils/helpers'

import Block from './Block'
function PassedView({ quiz, gradedQuiz }) {
  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold">You passed the quiz! 🎉</h2>
      <p className="text-xl">
        Score: {scoreFormatter(gradedQuiz?.score, quiz.numQuestionsToDisplay)}.
      </p>
      <p>
        You answered {gradedQuiz?.score} out of {quiz.numQuestionsToDisplay}{' '}
        {quiz.numQuestionsToDisplay !== 1 ? 'questions' : 'question'} correctly.
      </p>
      <QuizResult quiz={quiz} gradedQuiz={gradedQuiz} />
    </div>
  )
}
PassedView.propTypes = {
  quiz: PropTypes.object.isRequired,
  gradedQuiz: PropTypes.object.isRequired,
}

function FailedView({ quiz, gradedQuiz, retakeQuiz, reviewMaterial }) {
  const remainingAttempts = quiz.numOfRetriesAllowed - (gradedQuiz?.attemptNumber ?? 0)

  const hasRemainingAttempts = remainingAttempts > 0

  return (
    <div className="flex flex-col gap-4">
      <h2 className="text-2xl font-bold">You did not pass the quiz.</h2>
      <p className="text-xl">
        Score: {scoreFormatter(gradedQuiz?.score, quiz.numQuestionsToDisplay)}.
      </p>
      <p>
        Your answered <strong>{gradedQuiz?.score}</strong> correctly, but you must answer at least{' '}
        <strong>{quiz.numQuestionsToPass}</strong>{' '}
        {quiz.numQuestionsToPass === 1 ? 'question' : 'questions'} correctly to continue.
      </p>

      {/* Brian reported students couldn't tell which question they missed, so
          the review below marks each one. The API withholds correct_answers on
          a failed attempt, so this shows what the student picked and whether it
          was right — never the answer itself (RSCX-385). */}
      {gradedQuiz?.answers?.length > 0 && (
        <div className="flex flex-col gap-2">
          <h3 className="text-lg font-bold">Review your answers</h3>
          <p className="text-muted-foreground text-sm">
            The questions you missed are marked below. Review the material, then retake the quiz.
          </p>
          <QuizResult quiz={quiz} gradedQuiz={gradedQuiz} />
        </div>
      )}

      {hasRemainingAttempts && (
        <>
          <p>
            You have {remainingAttempts} remaining{' '}
            {remainingAttempts === 1 ? 'attempt' : 'attempts'}.
          </p>
          <div className="flex gap-2">
            <Button disabled={remainingAttempts === 0} onClick={retakeQuiz}>
              Retake the quiz
            </Button>
            {reviewMaterial && <Button onClick={reviewMaterial}>Review course material</Button>}
          </div>
        </>
      )}
      {!hasRemainingAttempts && (
        <>
          <p className="font-bold">
            You have exceeded the number of allowed quiz attempts. Please review the material and
            try again.
          </p>
          {reviewMaterial && (
            <div>
              <Button onClick={reviewMaterial}>Review course material</Button>
            </div>
          )}
        </>
      )}
    </div>
  )
}
FailedView.propTypes = {
  quiz: PropTypes.object.isRequired,
  gradedQuiz: PropTypes.object.isRequired,
  retakeQuiz: PropTypes.func.isRequired,
  reviewMaterial: PropTypes.func,
}

function QuizBudgetBanner({ quiz, attemptCount }) {
  if (!quiz?.numOfRetriesAllowed) return null
  const nextAttempt = (attemptCount ?? 0) + 1
  const total = quiz.numOfRetriesAllowed
  return (
    <div className="border-primary/30 bg-primary/5 mb-4 flex flex-col gap-1 rounded-md border p-3 text-sm sm:flex-row sm:items-center sm:justify-between">
      <span>
        Attempt <strong>{nextAttempt}</strong>
        {/* The backend allows retakes past the budget after re-reviewing the
            material, so "of N" only makes sense while within it. */}
        {nextAttempt <= total && (
          <>
            {' '}
            of <strong>{total}</strong>
          </>
        )}
      </span>
      {quiz.numQuestionsToPass && quiz.numQuestionsToDisplay && (
        <span className="text-muted-foreground">
          Pass with <strong>{quiz.numQuestionsToPass}</strong> of{' '}
          <strong>{quiz.numQuestionsToDisplay}</strong> correct
        </span>
      )}
    </div>
  )
}
QuizBudgetBanner.propTypes = {
  quiz: PropTypes.object.isRequired,
  attemptCount: PropTypes.number,
}

function BlockQuiz({ block, nextBlock, reviewMaterial, entityId, isLastBlock, upNextTitle }) {
  const instructorView = useInstructorView()
  const [canProceed, setCanProceed] = useState(false)
  /** @type {ReturnType<typeof useState<'quiz' | 'passed' | 'failed' | 'loading'>>} */
  const [step, setStep] = useState(() => (block.status === 'Completed' ? 'loading' : 'quiz'))
  const [quizResponse, setQuizResponse] = useState(null)
  const handleError = (m) => toast(m, 'error')

  const { data: pastQuizResponse } = useQuery({
    queryKey: ['accounts', 'students', 'course', 'quiz', block.id],
    queryFn: () => getQuizResult(entityId, block.id),
    enabled: ['loading', 'failed'].includes(step),
  })

  useEffect(() => {
    if (step === 'loading' && pastQuizResponse !== undefined) {
      setQuizResponse(pastQuizResponse)
      if (pastQuizResponse.result === 'Passed') {
        setStep('passed')
        setCanProceed(true)
      }
    }
  }, [step, pastQuizResponse])

  // Prevent closing while quiz is active, but the cleanup function will re-allow exiting
  useEffect(() => {
    course.setCanExit(!['loading', 'quiz'].includes(step))
    return () => course.setCanExit(true)
  }, [step])

  const handleQuizResponse = (response) => {
    setQuizResponse(response)
    if (response.result === 'Passed') {
      setStep('passed')
      setCanProceed(true)
      if (!instructorView) toast('Quiz passed.', 'success')
    } else if (response.result === 'Failed') {
      setStep('failed')
      setCanProceed(false)
    }
  }

  const queryClient = useQueryClient()

  const startCourseMutation = useMutation({
    mutationFn: (answers) => takeStudentQuiz(entityId, block.id, answers),
    onError: (err) => handleError(err.message),
    onSuccess: (response) => {
      handleQuizResponse(response)
      queryClient.invalidateQueries({ queryKey: ['accounts', 'students', 'courses'] })
    },
  })

  // Keep quizResponse around so the budget banner can count from the attempt
  // just recorded — block.quizAttempts won't reflect it until a refetch lands.
  const retakeQuiz = () => {
    setStep('quiz')
    setCanProceed(false)
  }

  const { quiz, type } = block.content

  // quizAttempts is a bare count for students but a full attempt list for
  // entity managers (the serializer branches on role, not on view) — a manager
  // taking their own course gets the list shape here. Normalize to a count.
  const priorAttempts = Array.isArray(block.quizAttempts)
    ? block.quizAttempts.length
    : block.quizAttempts

  // Instructor view is always read-only — never let an instructor's click
  // record a quiz attempt against the student's record. Show the attempts
  // panel instead of the interactive quiz.
  if (instructorView) {
    return (
      <Block
        canProceed
        nextBlock={nextBlock}
        title={block.name ?? quiz.name ?? 'Quiz'}
        isLastBlock={isLastBlock}
        block={block}
        upNextTitle={upNextTitle}
      >
        <div className="flex flex-col gap-4">
          <p className="text-muted-foreground italic">
            Read-only view — quiz submission is disabled.
          </p>
          <QuizAttemptsPanel contentRecord={block} entityId={entityId} />
        </div>
      </Block>
    )
  }

  return (
    <Block
      canProceed={canProceed}
      nextBlock={nextBlock}
      title={block.name ?? quiz.name ?? 'Quiz'}
      isLastBlock={isLastBlock}
      block={block}
      upNextTitle={upNextTitle}
    >
      {step === 'quiz' && (
        <>
          {/* quizResponse covers attempts made this session; priorAttempts
              covers ones from before it. */}
          <QuizBudgetBanner
            quiz={quiz}
            attemptCount={quizResponse?.attemptNumber ?? priorAttempts}
          />
          <Quiz
            key={quizResponse?.attemptNumber || '0'}
            quizData={quiz}
            onSubmit={(answers) => startCourseMutation.mutate(answers)}
          />
        </>
      )}
      {step === 'passed' && <PassedView quiz={quiz} gradedQuiz={quizResponse} />}
      {step === 'failed' && (
        <FailedView
          quiz={quiz}
          gradedQuiz={quizResponse}
          retakeQuiz={retakeQuiz}
          reviewMaterial={type === 'Quiz Only' ? null : reviewMaterial}
        />
      )}
      {step === 'loading' && <p>Checking quiz results...</p>}
    </Block>
  )
}
export default BlockQuiz

BlockQuiz.propTypes = {
  block: PropTypes.object.isRequired,
  nextBlock: PropTypes.func.isRequired,
  reviewMaterial: PropTypes.func.isRequired,
  entityId: PropTypes.string.isRequired,
  isLastBlock: PropTypes.bool,
  upNextTitle: PropTypes.string,
}
